import Decimal from "decimal.js";

/**
 *
 * This function generates a Random number from a given range
 *
 * @param minRange - Max value of the range
 * @param maxRange - Min value of the range
 * @returns - return an integer random number
 */
export const generateRandomNumber = (minRange: number, maxRange: number): number => {
  return Math.floor(Math.random() * (maxRange - minRange + 1)) + minRange;
};

// If we pass the amount as '$1000.20' or 'EUR1000.20'
// as string instead of a number,
// use this to get the number back
export const getNumberFromAmount = (amount: string | number) => {
  if (typeof amount === "number") return amount;
  return +amount.replace(/[$a-z]+/i, "").replace(/\s/g, "");
};

export const euclideanDivision = (a: number, b: number): [number, number] => {
  const q = Math.floor(a / b);
  const r = a - b * q;
  return [q, r];
};

/**
 * Adds thousand separators (spaces) to the integer part of a number string.
 * Preserves the decimal part exactly as-is without any rounding.
 */
function addThousandSeparators(value: string): string {
  const parts = value.split(".");
  const integerPart = parts[0] || "0";
  const decimalPart = parts[1];

  // Handle negative numbers
  const isNegative = integerPart.startsWith("-");
  const absoluteInteger = isNegative ? integerPart.slice(1) : integerPart;

  // Add thousand separators (spaces) from right to left
  const formattedInteger = absoluteInteger.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const result = isNegative ? "-" + formattedInteger : formattedInteger;

  return decimalPart !== undefined ? result + "." + decimalPart : result;
}

/**
 * Formats a number string with thousand separators while preserving full decimal precision.
 * Uses Decimal.js internally to avoid JavaScript's floating-point precision issues.
 *
 * @param value - The string value to format
 * @param precision - Maximum decimal places (default 6, use 18 for crypto tokens)
 * @returns Formatted string with thousand separators
 */
export const formatNumberAsString = (value: string, precision = 6) => {
  // Remove any existing whitespace
  value = value.replace(/\s/g, "");

  // If the value is an empty string, return "0"
  if (value === "") {
    return "0";
  }

  // Handle multiple decimal points - remove trailing one
  const hasMoreDecimalPoints = (value.match(/\./gi) ?? "").length > 1;
  if (hasMoreDecimalPoints && value.endsWith(".")) {
    const newAmount = value.substring(0, value.length - 1);
    return newAmount;
  }

  // Track if value ends with trailing zeros after decimal (user typing "1.0", "1.10", etc.)
  const decimalZero = value.includes(".") && value.endsWith("0");
  // Track if value ends with decimal point (user typing "1.")
  const endsWithDecimal = value.endsWith(".");

  // Get current decimal places
  const decimalPlaces = value.includes(".") ? (value.split(".")[1] || "").length : 0;

  // Truncate to precision limit (string manipulation to preserve precision)
  if (decimalPlaces > precision) {
    const parts = value.split(".");
    parts[1] = (parts[1] || "").slice(0, precision);
    value = parts.join(".");
  }

  // For values ending with trailing zeros or decimal, preserve the exact string
  // Just add thousand separators to the integer part
  if (decimalZero || endsWithDecimal) {
    return addThousandSeparators(value);
  }

  // Validate the number using Decimal.js (preserves full precision unlike parseFloat)
  try {
    const testValue = value.endsWith(".") ? value + "0" : value;
    const decimal = new Decimal(testValue);

    // Check for infinity
    if (!decimal.isFinite()) {
      return value;
    }

    // Add thousand separators while preserving full precision
    return addThousandSeparators(value);
  } catch {
    // Invalid number format, return as-is
    return value;
  }
};

export const percentageChange = (a: number, b: number): number | undefined => {
  if (a === 0) {
    return undefined;
  }
  return ((b - a) / a) * 100;
};

// Format precision values to avoid scientific notation
export const formatPrecision = (precision: number): string => {
  if (precision >= 1) {
    return precision.toString();
  }

  // For small decimal values, use toFixed to avoid scientific notation
  const decimalPlaces = Math.max(0, -Math.log10(precision));
  return precision.toFixed(decimalPlaces);
};
